import { CommunicationThreadTopic, COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS } from '@bariendo/lib/constants';
import { ContactPoint, Encounter, Reference } from '@medplum/fhirtypes';
import { useCallback, useState } from 'react';
import { MessageBoxType } from '@/components/Timeline/types';

export interface UseMessageBoxResult {
  topicMap: Record<CommunicationThreadTopic, string>;
  topic: CommunicationThreadTopic;
  selectedPhone: ContactPoint | undefined;
  showMessageInput: boolean;
  autofocusMessageBox: boolean;
  messageBoxVariant: MessageBoxType;
  sentMessage: boolean;
  getTopicLabel: (topic: CommunicationThreadTopic) => string;
  setTopic: (topic: CommunicationThreadTopic) => void;
  setSelectedPhone: (selectedPhone: ContactPoint | undefined) => void;
  setShowMessageInput: (showMessageInput: boolean) => void;
  setAutofocusMessageBox: (autofocusMessageBox: boolean) => void;
  setMessageBoxVariant: (messageBoxVariant: MessageBoxType) => void;
  setSentMessage: (sentMessage: boolean) => void;
  editingEncounterReference: Reference<Encounter> | null;
  setEditingEncounterReference: (encounter: Reference<Encounter> | null) => void;
}

const TOPIC_LABEL_MAP: Record<CommunicationThreadTopic, string> = COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS;

export function useMessageBox(): UseMessageBoxResult {
  const [topic, setTopic] = useState<CommunicationThreadTopic>(CommunicationThreadTopic.CLINICAL);
  const [selectedPhone, setSelectedPhone] = useState<ContactPoint | undefined>(undefined);
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [autofocusMessageBox, setAutofocusMessageBox] = useState(false);
  const [messageBoxVariant, setMessageBoxVariant] = useState<MessageBoxType>(MessageBoxType.SMS);
  const [sentMessage, setSentMessage] = useState(false);
  const [editingEncounterReference, setEditingEncounterReference] = useState<Reference<Encounter> | null>(null);
  const getTopicLabel = useCallback((topic: CommunicationThreadTopic) => {
    return topic in TOPIC_LABEL_MAP ? TOPIC_LABEL_MAP[topic] : 'Unknown';
  }, []);

  const handleSetShowMessageInput = useCallback(
    (value: boolean) => {
      if (!value) {
        setEditingEncounterReference(null);
      }
      setShowMessageInput(value);
    },
    [setEditingEncounterReference, setShowMessageInput]
  );

  return {
    topicMap: TOPIC_LABEL_MAP,
    topic,
    selectedPhone,
    showMessageInput,
    autofocusMessageBox,
    messageBoxVariant,
    sentMessage,
    setTopic,
    getTopicLabel,
    setSelectedPhone,
    setShowMessageInput: handleSetShowMessageInput,
    setAutofocusMessageBox,
    setMessageBoxVariant,
    setSentMessage,
    editingEncounterReference,
    setEditingEncounterReference,
  };
}
